import { useQuery } from '@apollo/client';
import { Notification } from '@prenuvo/shared-library';
import React, { createContext, useState } from 'react';
import { ToastProvider } from 'react-toast-notifications';

import { currentGraphQLErrorVar } from '../client';
import { ErrorPopup, LoadingScreen } from '../components';
import Modal, { ModalCTAType } from '../components/Modal';
import { HAS_GQL_ERROR } from '../graphql/queries';

// @types
export type UIContextType = {
  errorMessage: string;
  setErrorMessage: (message: string) => void;
  errorTitle: string;
  setErrorTitle: (message: string) => void;
  loading: boolean;
  setLoading: (isLoading: boolean) => void;
  modalMessage: string;
  setModalMessage: (message: string) => void;
  modalTitle: string;
  setModalTitle: (message: string) => void;
  modalCTA?: ModalCTAType;
  setModalCTA: (ctas: any) => void;
  displayNoteRequiredModal: (handleClick: () => void, modalMessage?: string) => void;
};

// ----------------------------------------------------------------------

export const UIContext = createContext<UIContextType | undefined>(undefined);

// ----------------------------------------------------------------------

const UIProvider = ({ children }: { children: React.ReactNode }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [errorTitle, setErrorTitle] = useState('');

  const [loading, setLoading] = useState(false);

  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalCTA, setModalCTA] = useState<ModalCTAType | undefined>();

  // queries
  const { data: gqlErrorData } = useQuery(HAS_GQL_ERROR);

  const displayNoteRequiredModal = (handleClick: any, modalMessage?: string) => {
    setModalTitle('Internal NP Notes');

    setModalMessage(
      modalMessage ||
        'An internal NP note is required, in order to mark this consultation as not complete.',
    );

    setModalCTA({
      text: 'Internal Notes',
      handleClick: handleClick,
    });
  };

  return (
    <UIContext.Provider
      value={{
        errorMessage,
        setErrorMessage,
        errorTitle,
        setErrorTitle,
        loading,
        setLoading,
        modalMessage,
        setModalMessage,
        modalTitle,
        setModalTitle,
        modalCTA,
        setModalCTA,
        displayNoteRequiredModal,
      }}
    >
      <ToastProvider autoDismiss components={{ Toast: Notification }} placement="bottom-right">
        <>{children}</>
      </ToastProvider>

      {errorMessage && (
        <ErrorPopup
          title={errorTitle}
          onClose={() => {
            setErrorMessage('');
            setErrorTitle('');
          }}
          message={errorMessage}
        />
      )}
      {modalMessage && (
        <Modal
          title={modalTitle}
          onClose={() => {
            setModalMessage('');
            setModalTitle('');
          }}
          message={modalMessage}
          modalCTA={modalCTA}
        />
      )}

      {gqlErrorData.currentGraphQLError && gqlErrorData.currentGraphQLError.showError ? (
        <ErrorPopup
          title={gqlErrorData.currentGraphQLError.headerText}
          message={gqlErrorData.currentGraphQLError.bodyText}
          onClose={() =>
            currentGraphQLErrorVar({
              headerText: '',
              bodyText: '',
              showError: false,
            })
          }
        />
      ) : null}

      {loading && <LoadingScreen />}
    </UIContext.Provider>
  );
};

export default UIProvider;
